import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, MapPin, Maximize, Building2, CircleDot } from "lucide-react";
import api from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import { Reveal } from "@/components/motion";

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/properties/${id}`).then(({ data }) => setProperty(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6">
      <p className="font-display text-3xl">Property not found.</p>
      <Link to="/" className="overline link-underline">Back to portfolio</Link>
    </div>
  );

  if (!property) return <div className="min-h-screen flex items-center justify-center overline text-muted-foreground">Loading…</div>;

  const specs = [
    { icon: Building2, label: "Type", value: property.property_type },
    { icon: Maximize, label: "Size", value: `${property.size_sqft.toLocaleString()} m²` },
    { icon: MapPin, label: "Location", value: property.location },
    { icon: CircleDot, label: "Status", value: property.status },
  ];

  return (
    <main className="pt-20">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-10">
        <Link to="/#portfolio" data-testid="back-link" className="inline-flex items-center gap-2 overline link-underline">
          <ArrowLeft size={16} /> Portfolio
        </Link>
      </div>

      {/* Title */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-10 pb-12">
        <div className="overline text-muted-foreground mb-4">{property.price}</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.19,1,0.22,1] }}
          data-testid="property-title"
          className="font-display text-4xl md:text-7xl font-extrabold tracking-tighter leading-none max-w-4xl"
        >
          {property.title}
        </motion.h1>
      </section>

      {/* Hero image with parallax */}
      <section ref={imgRef} className="overflow-hidden aspect-[16/9] max-h-[80vh] bg-muted">
        <motion.img
          src={property.images?.[0]} alt={property.title}
          style={{ scale }}
          className="h-full w-full object-cover"
        />
      </section>

      {/* Specs + description */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <Reveal className="overline text-muted-foreground mb-6">Overview</Reveal>
          <Reveal delay={0.05}>
            <p className="font-display text-2xl md:text-3xl font-light leading-snug tracking-tight">{property.description}</p>
          </Reveal>
        </div>

        <aside className="lg:col-span-5">
          <div className="border-t border-foreground">
            {specs.map((s) => (
              <div key={s.label} className="flex items-center justify-between py-5 border-b border-border">
                <span className="flex items-center gap-3 overline text-muted-foreground">
                  <s.icon size={16} /> {s.label}
                </span>
                <span className="font-display text-lg capitalize">{s.value}</span>
              </div>
            ))}
          </div>
        </aside>
      </section>

      {/* Gallery */}
      {property.images?.length > 1 && (
        <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-20">
          <Reveal className="overline text-muted-foreground mb-8">Gallery · {property.images.length} photos</Reveal>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-[240px] md:auto-rows-[320px]">
            {property.images.slice(1).map((src, i) => (
              <Reveal
                key={i}
                delay={(i % 3) * 0.06}
                className={`group overflow-hidden bg-muted border border-border ${i % 5 === 0 ? "md:col-span-4" : "md:col-span-2"}`}
              >
                <img
                  src={src}
                  alt={`${property.title} — ${i + 2}`}
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105"
                />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Enquiry */}
      <section className="bg-secondary/40 border-t border-border py-20 lg:py-28">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline text-muted-foreground mb-6">Enquire · {property.title}</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-none">
              Request a<br />private viewing.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <InquiryForm property={property} />
          </div>
        </div>
      </section>
    </main>
  );
}
